import React from 'react';
import { Box, Button, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { dataAsOfDay } from '../dialing-opportunity/components/PeriodComparePicker';

const ISO = 'YYYY-MM-DD';

export interface CohortRange {
  start: string;
  end: string;
}

/**
 * Date range for the cohort table, deliberately its own control rather
 * than ScorecardPeriodPicker.
 *
 * That picker's presets are calendar-aligned because the Scorecard's trend
 * grain is derived from whether the selection *is* a calendar week, month
 * or quarter. The cohort table has no trend, so that constraint buys
 * nothing here -- what matters instead is how far back you look, which is
 * what these presets express.
 *
 * The two live on the same page and must stay independent: changing the
 * cohort range must not refetch the Scorecard, and vice versa.
 *
 * Every preset ends no later than yesterday. The warehouse loads a day in
 * arrears, so including today would count a partial day of arrivals as a
 * full one and understate every rate in the newest row.
 */
interface PresetDef {
  id: string;
  label: string;
  /** What the row grain will become, so the consequence of a wide range is
   * visible before it is picked rather than discovered afterwards. */
  grain: string;
  range: () => { start: Dayjs; end: Dayjs };
}

const PRESETS: PresetDef[] = [
  {
    id: 'last_7',
    label: 'Last 7 days',
    grain: 'daily rows',
    range: () => ({ start: dataAsOfDay().subtract(6, 'day'), end: dataAsOfDay() }),
  },
  {
    id: 'last_14',
    label: 'Last 14 days',
    grain: 'daily rows',
    range: () => ({ start: dataAsOfDay().subtract(13, 'day'), end: dataAsOfDay() }),
  },
  {
    id: 'last_30',
    label: 'Last 30 days',
    grain: 'daily rows',
    range: () => ({ start: dataAsOfDay().subtract(29, 'day'), end: dataAsOfDay() }),
  },
  {
    id: 'this_month',
    label: 'This month to date',
    grain: 'daily rows',
    range: () => ({ start: dataAsOfDay().startOf('month'), end: dataAsOfDay() }),
  },
  {
    id: 'last_month',
    label: 'Last month',
    grain: 'daily rows',
    range: () => {
      const m = dataAsOfDay().subtract(1, 'month');
      return { start: m.startOf('month'), end: m.endOf('month') };
    },
  },
  {
    id: 'last_quarter',
    label: 'Last 90 days',
    grain: 'weekly rows',
    range: () => ({ start: dataAsOfDay().subtract(89, 'day'), end: dataAsOfDay() }),
  },
  {
    id: 'last_year',
    label: 'Last 12 months',
    grain: 'monthly rows',
    range: () => ({ start: dataAsOfDay().subtract(1, 'year').add(1, 'day'), end: dataAsOfDay() }),
  },
];

/** Last 7 days, matching PULSE_COHORT_LOOKBACK_DAYS so the two cohort
 * tables open on the same window and can be compared without first
 * re-picking a range on one of them. */
export function defaultCohortRange(): CohortRange {
  const { start, end } = PRESETS[0].range();
  return { start: start.format(ISO), end: end.format(ISO) };
}

function label(range: CohortRange): string {
  const s = dayjs(range.start);
  const e = dayjs(range.end);
  const sameYear = s.year() === e.year();
  return `${s.format(sameYear ? 'D MMM' : 'D MMM YYYY')} – ${e.format('D MMM YYYY')}`;
}

export default function CohortRangePicker({
  value,
  onChange,
  disabled,
}: {
  value: CohortRange;
  onChange: (next: CohortRange) => void;
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const [customStart, setCustomStart] = React.useState<Dayjs | null>(dayjs(value.start));
  const [customEnd, setCustomEnd] = React.useState<Dayjs | null>(dayjs(value.end));

  React.useEffect(() => {
    setCustomStart(dayjs(value.start));
    setCustomEnd(dayjs(value.end));
  }, [value.start, value.end]);

  const close = () => setAnchor(null);

  const applyPreset = (preset: PresetDef) => {
    const { start, end } = preset.range();
    onChange({ start: start.format(ISO), end: end.format(ISO) });
    close();
  };

  const applyCustom = () => {
    if (!customStart || !customEnd || customEnd.isBefore(customStart)) return;
    // Clamped here as well as server-side: a user who types a future date
    // should see the range they will actually get, not have it silently
    // corrected on the way back.
    const end = customEnd.isAfter(dataAsOfDay()) ? dataAsOfDay() : customEnd;
    onChange({ start: customStart.format(ISO), end: end.format(ISO) });
    close();
  };

  const customInvalid = !customStart || !customEnd || customEnd.isBefore(customStart);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        startIcon={<CalendarMonthRounded sx={{ fontSize: 16 }} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}
        aria-label="Change the cohort date range"
      >
        {label(value)}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {PRESETS.map((preset) => (
          <MenuItem key={preset.id} onClick={() => applyPreset(preset)} sx={{ py: 0.75 }}>
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {preset.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {preset.grain}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ px: 2, py: 1.5, width: 268 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }} color="text.disabled">
            Custom range
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
              <DatePicker
                label="First arrival day"
                value={customStart}
                maxDate={dataAsOfDay()}
                onChange={setCustomStart}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="Last arrival day"
                value={customEnd}
                minDate={customStart ?? undefined}
                maxDate={dataAsOfDay()}
                onChange={setCustomEnd}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Button size="small" variant="contained" onClick={applyCustom} disabled={customInvalid}>
                Apply
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Menu>
    </>
  );
}
